import { ArticleColoring } from "../ViewerRedux/ArticleColoring";
import { selectActiveArticleColoring } from "../ViewerRedux/articleColoringSelectors";
import { useInitialModuleRoom } from "./useInitialModuleRoom";
import React, { useEffect, useState } from "react";
import { joinClassNames } from "../utils/test-utils";
import { useResizable } from "react-resizable-layout";
import "./ResizeableLayoutDemo.css";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectProjectNo,
  setFloorAndJobInRedux,
  setMeasurementToolActiveInRedux,
  toggleShowTrueSizeOfDevicesInRedux,
  toggleSnapToGridInRedux,
} from "./designSlice";
import { clearDesignThunk } from "./designThunk";
import { RoomSideBar } from "../RoomSideBar/RoomSideBar";
import { LeftSidebar } from "./LeftSidebar/LeftSidebar";
import SampleSplitter from "./SampleSplitter";
import { Box, Transition } from "@mantine/core";
import ReviewBar from "./ReviewBar";
import ResultAlgosBar from "./ResultAlgosBar";
import UnsavedPipesBar from "./routing/UnsavedPipesBar";
import { useHotkeys } from "@mantine/hooks";
import { HOTKEYS } from "../constants";
import { ValidationBar } from "./ValidationBar";
import { copyDeviceThunk, setSelectedDeviceId } from "./devices/deviceSlice";
import { selectClickedMcBimItemId, setClickedMcBimItemIdInRedux } from "../ViewerRedux/viewerSlice";
import {
  useDeleteMultiplacerDeviceDeleteDeviceMutation,
  useGetMcbimBuildingStoriesQuery,
  useGetMcbimDisciplinesQuery,
  useGetMultiplacerDevicetypesQuery,
  useGetMultiplacerViewsQuery,
  useGetRoutingGraphListQuery,
  usePostMultiplacerRoomsQuery,
} from "../../openapi";
import AlgoProcessBar from "./AlgoProgressBar";
import { useGetDevices } from "../hooks/useGetDevices";
import { useGetMcbimSpaces } from "../hooks/useGetMcBimSpaces";
import { DesignTabViewer } from "./Viewer/DesignTabViewer";
import { WelcomeFloorPicker } from "./WelcomeFloorPicker";
import { useSearchParams } from "react-router-dom";
import { useAppUrlStateSync } from "../hooks/useAppUrlStateSync";
import { GlobalDesignBar } from "./GlobalDesignBar/GlobalDesignBar";
import { DrofusOccurrenceValidationBar } from "./ModuleBars/DrofusOccurrenceValidationBar/DrofusOccurrenceValidationBar";
import { CeilingBar } from "./ModuleBars/CeilingBar/CeilingBar";
import { RoomNumberingBar } from "./ModuleBars/RoomNumberingBar/RoomNumberingBar";
import { AutoPipeRoutingConnectionPointBanner } from "./AutoPipeRoutingConnectionPointBanner";
import { FloorViewerHelpBannerContainer } from "./FloorViewerHelpBannerContainer";
import { CopySpaceModeBanner } from "./CopySpaceModeBanner";
import { skipToken } from "@reduxjs/toolkit/query";
import { RoomColoringResultChart } from "../RoomColoring/RoomColoringResultChart";
import { useUndoRedoHotkeys } from "./history/useUndoRedoHotkeys";
import { MmiLegend } from "./MMILegend/MmiLegend";
import { selectProjectnoAndBuildingStoryArgs } from "./designSelector";
import { PrimarySystemLegend } from "./PrimarySystemLegend";
import { NoComponentsVisibleSearchBanner } from "../NoComponentsVisibleSearchBanner";
import { RoomSearchResultsBanner } from "../RoomSearchResultsBanner";
import { ComponentSearchResultsBanner } from "../components/ComponentSearchResultsBanner";
import { selectGetRoutingGraphsArgs } from "./routing/routingSelectors";
import { selectGetRoomsArgs } from "../Room/httpRoomSelectors";
import { selectTrendByKeyArgsForRoomColoring } from "../RoomColoring/roomColoringSelectors";
import { selectAutoPipeRoutingRunState } from "./pipeRoutingSlice";
import { selectIsRoomHighlightActive } from "./roomHighlightSelectors";
import { DuplicateSpacesBanner } from "./DuplicateSpacesBanner";
import { useSharedViewUrl } from "./SharedViews/useSharedViewUrl";
import { selectViewPresetsQueryArgs } from "./viewPresetSelectors";

const DesignTab: React.FC = () => {
  const [isStreetViewActive, setIsStreetViewActive] = useState(false);
  useAppUrlStateSync({ module: false });
  useSharedViewUrl();
  useInitialModuleRoom();
  const dispatch = useAppDispatch();
  useUndoRedoHotkeys();
  const projectNo = useAppSelector(selectProjectNo);
  const isRoomHighlightActive = useAppSelector(selectIsRoomHighlightActive);
  const argsDisciplines = useAppSelector(selectProjectnoAndBuildingStoryArgs);
  useGetMcbimDisciplinesQuery(argsDisciplines ?? skipToken);
  const activeModule = useAppSelector((state) => state.design.activeModule);
  const viewPresetsArgs = useAppSelector(selectViewPresetsQueryArgs);
  const needsSavedViewPreset = useAppSelector(state => state.viewer.selectedViewPreset?.source === "user"
    || state.viewer.viewSettingsPreset?.source === "user");
  useGetMultiplacerViewsQuery(needsSavedViewPreset ? viewPresetsArgs : skipToken);
  const [searchParams] = useSearchParams();
  const { selectedDeviceId } = useAppSelector((state) => state.device);
  const clickedMcBimItemId = useAppSelector(selectClickedMcBimItemId);
  const devices = useGetDevices();
  const selectedDevice = devices.find((device) => device._id === selectedDeviceId);
  const getRoomArgs = useAppSelector(selectGetRoomsArgs);

  const activeArticleColoring = useAppSelector(selectActiveArticleColoring);
  const mcbimItemColorMode = useAppSelector((s) => s.design.mcbimItemColorMode);

  const { data: rooms = {} } = usePostMultiplacerRoomsQuery(getRoomArgs ?? skipToken);

  const roomInSelectedDevice = rooms[selectedDevice?.roomNumber as string];
  const roomIsLocked = roomInSelectedDevice?.multiplacerCeilingDevicesLocked?.bool;
  const mcbimSpaces = useGetMcbimSpaces();
  const selectedSpaceNumber = useAppSelector((state) => state.design.selectedSpaceNumber);
  const copySpaceModeActive = useAppSelector((state) => state.design.copySpaceModeActive);
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);

  const argsRoutingGraph = useAppSelector(selectGetRoutingGraphsArgs);
  useGetRoutingGraphListQuery(argsRoutingGraph ?? skipToken);

  const roomColoringTrendAvailable = useAppSelector(selectTrendByKeyArgsForRoomColoring) != null;

  const { data: deviceTypes } = useGetMultiplacerDevicetypesQuery(
    { projectNo: projectNo },
    { skip: activeModule !== "CEILING" }
  );

  const urlFloor = searchParams.has("state") ? null : searchParams.get("floor");
  const urlBuilding = searchParams.get("building");
  const { data: buildingStories = [] } = useGetMcbimBuildingStoriesQuery(
    { projectNo: projectNo },
    { skip: urlFloor == null }
  );


  useEffect(() => {
    if (urlFloor && buildingStories.length > 0) {
      const matchingStory = buildingStories.find((story) =>
        story.name === urlFloor && (urlBuilding == null || story.building?.trim() === urlBuilding)
      );
      if (matchingStory) {
        dispatch(
          setFloorAndJobInRedux({
            building: matchingStory.building,
            floorName: matchingStory.name,
            jobId: matchingStory.job.jobId,
            floorGlobalId: matchingStory.globalId,
            elevation: matchingStory.elevation ?? undefined,
          })
        );
      } else {
        console.log("Ingen matchende etasje for:", urlFloor);
      }
    }
  }, [urlFloor, urlBuilding, buildingStories, dispatch]);

  const activeLeftSideBarTab = useAppSelector((state) => state.design.activeLeftSideBarTab);

  const {
    isDragging: isFileDragging,
    position: fileW,
    splitterProps: fileDragBarProps,
  } = useResizable({
    axis: "x",
    initial: 500,
    min: 200,
    max: 700,
  });
  const roomChartLeft = activeLeftSideBarTab == null ? 55 : fileW + 19;

  const {
    isDragging: isPluginDragging,
    position: pluginW,
    splitterProps: pluginDragBarProps,
  } = useResizable({
    axis: "x",
    initial: 600,
    min: 180,
    max: 700,
    reverse: true,
  });

  const algosInProgress = useAppSelector((state) => state.design.algosInProgress);
  const review = useAppSelector((state) => state.design.review);
  const spaceValidationRun = useAppSelector((state) => state.design.spaceValidationRun);
  const nonPublischedDevicesFromAlgos = useAppSelector(
    (state) => state.design.nonPublischedDevicesFromAlgos
  );
  const unsavedPipes = useAppSelector((state) => state.unsavedRouting.items);
  const autoPipeRoutingRunState = useAppSelector(selectAutoPipeRoutingRunState);

  const [deleteSingleDevice] = useDeleteMultiplacerDeviceDeleteDeviceMutation();

  const handleDeleteSelected = () => {
    if (roomIsLocked) {
      console.log("room is locked");
      return;
    }
    deleteSingleDevice({ deviceId: selectedDeviceId as string });
  };

  useHotkeys([
    [HOTKEYS.SNAP_TO_GRID, () => dispatch(toggleSnapToGridInRedux())],
    [HOTKEYS.TRUE_SIZE, () => dispatch(toggleShowTrueSizeOfDevicesInRedux())],
    [HOTKEYS.MEASURE, () => dispatch(setMeasurementToolActiveInRedux(true))],
    [HOTKEYS.ABORT, () => {
      if (selectedDeviceId != null || clickedMcBimItemId != null) {
        dispatch(setSelectedDeviceId(null));
        dispatch(setClickedMcBimItemIdInRedux(null));
        return;
      }
      dispatch(clearDesignThunk());
    }],
    [HOTKEYS.PASTE_PRODUCT, () => dispatch(copyDeviceThunk(selectedDeviceId as string))],
    // ✅ både Backspace och Delete triggar samma
    [HOTKEYS.DELETE_PRODUCT, handleDeleteSelected],
    ["delete", handleDeleteSelected],
  ]);
  return (
    <div
      className={"flex flex-column h-screen font-mono color-white overflow-hidden"}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        userSelect: isFileDragging || isPluginDragging ? "none" : "auto",
      }}
    >
      {floorAndJob == null && <WelcomeFloorPicker />}

      {mcbimSpaces != null && floorAndJob != null && (
        <>
          <GlobalDesignBar />
          <ArticleColoring />
          {!activeArticleColoring && mcbimItemColorMode === "MMI" && <MmiLegend />}
          {!activeArticleColoring && (mcbimItemColorMode === "PRIMARY_SYSTEM" || mcbimItemColorMode === "SYSTEM_AND_CLASS") && <PrimarySystemLegend />}
          {activeModule === "ROOM_VALIDATION" && <DrofusOccurrenceValidationBar />}
          {activeModule === "CEILING" && <CeilingBar />}
          {activeModule === "ROOM_NUMBERING" && <RoomNumberingBar />}
          {spaceValidationRun.active && <ValidationBar />}
          {review.active && <ReviewBar />}
          {/* {showRoomBar && <RoomsBar />} */}

          {algosInProgress && <AlgoProcessBar />}
          {nonPublischedDevicesFromAlgos.length > 0 && <ResultAlgosBar />}
          {unsavedPipes.length > 0 && !autoPipeRoutingRunState.isRunning && <UnsavedPipesBar />}
          <DuplicateSpacesBanner />

          <div
            className={"flex grow"}
            style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}
          >
            {/* Venstre side */}
            <div
              style={{
                visibility: isRoomHighlightActive ? "hidden" : "visible",
                width: "auto",
                top: 0,
                bottom: 0,
                display: "flex",
                position: "absolute",
                zIndex: 1,
              }}
            >
              <div
                className={joinClassNames("shrink-0 contents", isFileDragging && "dragging")}
                style={{
                  width: activeLeftSideBarTab == null ? 36 : fileW,
                  height: "100%",
                  boxShadow: "#4d4d4d -1px 4px 4px 0px",
                }}
              >
                <LeftSidebar />
              </div>
              {activeLeftSideBarTab && (
                <SampleSplitter isDragging={isFileDragging} {...fileDragBarProps} />
              )}
            </div>

            {/* Midten */}
            <div style={{ height: "100%", flex: 1, minWidth: 0 }}>
              <DesignTabViewer onStreetViewActiveChange={setIsStreetViewActive} />
              <RoomColoringResultChart
                collapsible
                style={{
                  visibility: isRoomHighlightActive ? "hidden" : "visible",
                  position: "absolute",
                  bottom: 16,
                  left: roomChartLeft,
                  maxWidth: `calc(100% - ${roomChartLeft + 16}px)`,
                  zIndex: 20,

                  // ✅ toggle size here
                  width: roomColoringTrendAvailable ? "800px" : "400px",

                  pointerEvents: "auto",
                }}
              />
            </div>
            <FloorViewerHelpBannerContainer>
              <CopySpaceModeBanner />
              <AutoPipeRoutingConnectionPointBanner />
              <NoComponentsVisibleSearchBanner />
              <RoomSearchResultsBanner />
              <ComponentSearchResultsBanner />
            </FloorViewerHelpBannerContainer>

            {/* Høyre side */}
            <div
              style={{
                visibility: isRoomHighlightActive ? "hidden" : "visible",
                width: "auto",
                top: 0,
                bottom: 0,
                display: "flex",
                position: isStreetViewActive && !isRoomHighlightActive ? "relative" : "absolute",
                flexShrink: 0,
                zIndex: 1,
                right: 0,
              }}
            >
              {selectedSpaceNumber != null && !copySpaceModeActive && (
                <SampleSplitter isDragging={isPluginDragging} {...pluginDragBarProps} />
              )}

              <Transition
                mounted={selectedSpaceNumber != null && !copySpaceModeActive}
                transition="slide-left"
                duration={300}
                timingFunction="ease"
              >
                {(styles) => (
                  <Box
                    h="100%"
                    className={joinClassNames("shrink-0", isPluginDragging && "dragging")}
                    style={{
                      ...styles, // transform + opacity settes av Mantine
                      width: pluginW,
                      boxShadow: "#4d4d4d 1px 4px 4px 0px",
                    }}
                  >
                    <RoomSideBar />
                  </Box>
                )}
              </Transition>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DesignTab;
